import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Bot, Save, FileText, MessageSquare, BarChart3, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { pluginApi, aiHelperApi, serverApi } from "@/lib/api";

interface AIHelperPluginProps {
  serverId: string;
}

interface ServerRegulation {
  id: string;
  serverId: string;
  title: string;
  content: string;
  channelId?: string | null;
  requiredRoleId?: string | null;
  aiEnabled?: boolean;
  lastUpdated: string;
  updatedBy?: string;
}

interface AIStats {
  totalSessions: number;
  activeSessions: number;
  totalMessages: number;
  avgMessagesPerSession: number;
}

interface AIHelperPluginConfig {
  helperChannelId: string;
  rulesChannelId: string;
}

interface AIHelperPanelStatus {
  configured: boolean;
  helperChannelId: string;
  spawned: boolean;
  messageId: string | null;
  messageUrl: string | null;
}

interface AIRulesEmbedStatus {
  configured: boolean;
  rulesChannelId: string;
  spawned: boolean;
  messageId: string | null;
  messageUrl: string | null;
}

export function AIHelperPlugin({ serverId }: AIHelperPluginProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [regulationChannelId, setRegulationChannelId] = useState("");
  const [requiredRoleId, setRequiredRoleId] = useState("");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isCreatingNewRegulation, setIsCreatingNewRegulation] = useState(false);
  const [testQuestion, setTestQuestion] = useState("");
  const [testAnswer, setTestAnswer] = useState("");
  const [pluginConfig, setPluginConfig] = useState<AIHelperPluginConfig>({
    helperChannelId: '',
    rulesChannelId: '',
  });
  const queryClient = useQueryClient();

  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const { data: regulations = [], isLoading: loadingRules } = useQuery<ServerRegulation[]>({
    queryKey: ['ai-helper', serverId, 'regulations'],
    queryFn: async () => {
      try {
        return await aiHelperApi.getRegulations(serverId);
      } catch (error: any) {
        if (error?.status === 404) return [];
        throw error;
      }
    },
  });

  // Fetch stats
  const { data: stats } = useQuery<AIStats>({
    queryKey: ['ai-helper', serverId, 'stats'],
    queryFn: () => aiHelperApi.getStats(serverId),
  });

  const {
    data: panelStatus,
    isLoading: panelStatusLoading,
    refetch: refetchPanelStatus,
  } = useQuery<AIHelperPanelStatus>({
    queryKey: ['ai-helper', serverId, 'panel-status'],
    queryFn: () => aiHelperApi.getPanelStatus(serverId),
  });

  const {
    data: rulesEmbedStatus,
    isLoading: rulesEmbedStatusLoading,
    refetch: refetchRulesEmbedStatus,
  } = useQuery<AIRulesEmbedStatus>({
    queryKey: ['ai-helper', serverId, 'rules-status', selectedRuleId || 'default'],
    queryFn: () => aiHelperApi.getRulesEmbedStatus(serverId, selectedRuleId || undefined),
  });

  // Update selected regulation when loaded
  useEffect(() => {
    if (regulations.length === 0) {
      setIsCreatingNewRegulation(false);
      setSelectedRuleId(null);
      setTitle('');
      setContent('');
      setRegulationChannelId('');
      setRequiredRoleId('');
      setAiEnabled(true);
      return;
    }

    if (isCreatingNewRegulation) {
      return;
    }

    const selected = selectedRuleId
      ? regulations.find((item) => item.id === selectedRuleId)
      : regulations[0];

    if (selected) {
      setSelectedRuleId(selected.id);
      setTitle(selected.title);
      setContent(selected.content);
      setRegulationChannelId(String(selected.channelId || ''));
      setRequiredRoleId(String(selected.requiredRoleId || ''));
      setAiEnabled(selected.aiEnabled !== false);
    }
  }, [isCreatingNewRegulation, regulations, selectedRuleId]);

  useEffect(() => {
    const plugin = (plugins as any[])?.find((p: any) => p.pluginId === 'ai-helper');
    if (!plugin?.config) return;

    setPluginConfig({
      helperChannelId: String(plugin.config.helperChannelId || ''),
      rulesChannelId: String(plugin.config.rulesChannelId || ''),
    });
  }, [plugins]);

  const aiHelperPlugin = (plugins as any[])?.find((p: any) => p.pluginId === 'ai-helper');
  const isEnabled = aiHelperPlugin?.enabled ?? false;
  const selectedRegulation = regulations.find((item) => item.id === selectedRuleId) || null;
  const effectiveRulesChannelId = String((regulations.find((item) => item.id === selectedRuleId) || pluginConfig).channelId || pluginConfig.rulesChannelId || '').trim();

  const togglePluginMutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      enabled ? pluginApi.enablePlugin(serverId, 'ai-helper') : pluginApi.disablePlugin(serverId, 'ai-helper'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
    },
  });


  const saveRulesMutation = useMutation({
    mutationFn: async () => {
      if (selectedRuleId) {
        return aiHelperApi.updateRegulation(serverId, selectedRuleId, {
          title,
          content,
          channelId: regulationChannelId || undefined,
          requiredRoleId: requiredRoleId || undefined,
          aiEnabled,
        });
      }
      return aiHelperApi.createRegulation(serverId, {
        title,
        content,
        channelId: regulationChannelId || undefined,
        requiredRoleId: requiredRoleId || undefined,
        aiEnabled,
      });
    },
    onSuccess: (savedRegulation: any) => {
      if (savedRegulation?.id) {
        setSelectedRuleId(savedRegulation.id);
      }
      if (savedRegulation?.title) {
        setTitle(savedRegulation.title);
      }
      if (savedRegulation?.content) {
        setContent(savedRegulation.content);
      }
      setRegulationChannelId(String(savedRegulation?.channelId || ''));
      setRequiredRoleId(String(savedRegulation?.requiredRoleId || ''));
      setAiEnabled(savedRegulation?.aiEnabled !== false);
      setIsCreatingNewRegulation(false);
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'regulations'] });
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'rules-status'] });
    },
  });

  const deleteRegulationMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRuleId) return;
      await aiHelperApi.deleteRegulation(serverId, selectedRuleId);
    },
    onSuccess: () => {
      setIsCreatingNewRegulation(false);
      setSelectedRuleId(null);
      setTitle('');
      setContent('');
      setRegulationChannelId('');
      setRequiredRoleId('');
      setAiEnabled(true);
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'regulations'] });
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'rules-status'] });
    },
  });

  // Test AI mutation
  const testAIMutation = useMutation({
    mutationFn: async (question: string) => aiHelperApi.ask(serverId, question, selectedRuleId || undefined),
    onSuccess: (data) => {
      setTestAnswer(data.answer);
    },
  });

  const savePluginConfigMutation = useMutation({
    mutationFn: async () => pluginApi.updatePluginConfig(serverId, 'ai-helper', pluginConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'panel-status'] });
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'rules-status'] });
    },
  });

  const spawnPanelMutation = useMutation({
    mutationFn: async () => aiHelperApi.spawnPanel(serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'panel-status'] });
    },
  });

  const spawnRulesEmbedMutation = useMutation({
    mutationFn: async () => aiHelperApi.spawnRulesEmbed(serverId, selectedRuleId || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-helper', serverId, 'rules-status'] });
    },
  });

  const handleSaveRules = () => {
    if (!title.trim() || !content.trim()) {
      alert('Compila tutti i campi!');
      return;
    }
    saveRulesMutation.mutate();
  };

  const handleCreateNewRegulation = () => {
    setIsCreatingNewRegulation(true);
    setSelectedRuleId(null);
    setTitle('');
    setContent('');
    setRegulationChannelId('');
    setRequiredRoleId('');
    setAiEnabled(true);
  };

  const handleDeleteRegulation = () => {
    if (!selectedRuleId) return;
    if (!confirm('Eliminare questo regolamento?')) return;
    deleteRegulationMutation.mutate();
  };

  const handleTestAI = () => {
    if (!testQuestion.trim()) {
      alert('Scrivi una domanda!');
      return;
    }
    setTestAnswer('');
    testAIMutation.mutate(testQuestion);
  };

  const handleSavePluginConfig = () => {
    savePluginConfigMutation.mutate();
  };

  const handleSpawnPanel = () => {
    spawnPanelMutation.mutate();
  };

  const handleSpawnRulesEmbed = () => {
    spawnRulesEmbedMutation.mutate();
  };

  if (loadingRules) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Bot className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Helper - Assistente Regolamento</h2>
              <p className="text-muted-foreground">
                Configura l'AI che risponde alle domande degli utenti basandosi sul regolamento
              </p>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            disabled={togglePluginMutation.isPending}
            onCheckedChange={(enabled) => togglePluginMutation.mutate(enabled)}
          />
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Sessioni Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalSessions}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Sessioni Attive
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{stats.activeSessions}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Messaggi Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalMessages}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Media Msg/Sessione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgMessagesPerSession.toFixed(1)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rules Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <CardTitle>Regolamenti del Server</CardTitle>
          </div>
          <CardDescription>
            Gestisci più regolamenti: l'AI Helper userà quello selezionato o quello più rilevante
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium">Seleziona Regolamento</label>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
              <select
                value={selectedRuleId || ''}
                onChange={(e) => {
                  setIsCreatingNewRegulation(false);
                  setSelectedRuleId(e.target.value || null);
                }}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="" disabled>
                  {regulations.length === 0 ? 'Nessun regolamento disponibile' : 'Seleziona un regolamento'}
                </option>
                {regulations.map((regulation) => (
                  <option key={regulation.id} value={regulation.id}>
                    {regulation.title}
                  </option>
                ))}
              </select>

              <Button type="button" variant="outline" onClick={handleCreateNewRegulation}>
                {isCreatingNewRegulation ? 'Nuovo regolamento' : 'Nuovo'}
              </Button>

              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteRegulation}
                disabled={!selectedRuleId || deleteRegulationMutation.isPending}
              >
                {deleteRegulationMutation.isPending ? 'Eliminazione...' : 'Elimina'}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Titolo Regolamento</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es: Regolamento Ufficiale del Server"
              className="bg-background"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Contenuto (Markdown supportato)
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Scrivi qui il regolamento completo del server...&#10;&#10;Puoi usare Markdown:&#10;# Titolo&#10;## Sottotitolo&#10;- Lista&#10;**Grassetto**&#10;*Corsivo*"
              className="bg-background min-h-[400px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {content.length} caratteri • Ultimo aggiornamento:{" "}
              {selectedRegulation?.lastUpdated
                ? new Date(selectedRegulation.lastUpdated).toLocaleString("it-IT")
                : "Mai"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Canale specifico regolamento (opzionale)</label>
            <Select
              value={regulationChannelId || 'none'}
              onValueChange={(value) => setRegulationChannelId(value === 'none' ? '' : value)}
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona canale dedicato al regolamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Usa canale regolamento globale</SelectItem>
                {(channels as any[]).map((channel: any) => (
                  <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Ogni regolamento può avere il suo canale dedicato e il suo pulsante AI contestuale.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Ruolo whitelist del gioco (opzionale)</label>
            <Select
              value={requiredRoleId || 'none'}
              onValueChange={(value) => setRequiredRoleId(value === 'none' ? '' : value)}
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona ruolo richiesto per questo gioco" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun ruolo richiesto</SelectItem>
                {(roles as any[]).map((role: any) => (
                  <SelectItem key={role.id} value={role.id}>@{role.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Solo gli utenti con questo ruolo whitelist potranno usare l'AI su questo regolamento/gioco.
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer rounded-lg border p-3 bg-muted/20">
            <Switch checked={aiEnabled} onCheckedChange={setAiEnabled} />
            <div>
              <p className="text-sm font-medium">Mostra questo gioco nel menu selezione AI</p>
              <p className="text-xs text-muted-foreground">
                Flagbox per decidere se questo regolamento è selezionabile dal menu giochi dell'AI Helper.
              </p>
            </div>
          </label>

          <Button
            onClick={handleSaveRules}
            disabled={saveRulesMutation.isPending}
            className="w-full"
          >
            {saveRulesMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isCreatingNewRegulation || !selectedRuleId ? 'Crea Regolamento' : 'Salva Regolamento'}
              </>
            )}
          </Button>

          {saveRulesMutation.isSuccess && (
            <p className="text-sm text-green-500 text-center">
              ✅ Regolamento salvato con successo!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Channel Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            <CardTitle>Configurazione Canali AI Helper</CardTitle>
          </div>
          <CardDescription>
            Configura i canali usati da /ai-helper (salvataggio su configurazione plugin)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Canale AI Helper (opzionale)</label>
            <Select
              value={pluginConfig.helperChannelId || 'none'}
              onValueChange={(value) =>
                setPluginConfig((prev) => ({ ...prev, helperChannelId: value === 'none' ? '' : value }))
              }
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona canale AI Helper" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun canale</SelectItem>
                {(channels as any[]).map((channel: any) => (
                  <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Canale Regolamento (opzionale)</label>
            <Select
              value={pluginConfig.rulesChannelId || 'none'}
              onValueChange={(value) =>
                setPluginConfig((prev) => ({ ...prev, rulesChannelId: value === 'none' ? '' : value }))
              }
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona canale regolamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun canale</SelectItem>
                {(channels as any[]).map((channel: any) => (
                  <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSavePluginConfig}
            disabled={savePluginConfigMutation.isPending}
            className="w-full"
            variant="outline"
          >
            {savePluginConfigMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio configurazione...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salva Configurazione Canali
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Panel Spawn & Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <CardTitle>Spawn Pannello AI Helper</CardTitle>
          </div>
          <CardDescription>
            Pubblica l'embed con pulsante in chat e verifica se è già spawnato nel canale helper
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg border bg-muted/30 space-y-2">
            {panelStatusLoading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Caricamento stato pannello...
              </p>
            ) : (
              <>
                <p className="text-sm">
                  <span className="font-medium">Canale Helper:</span>{' '}
                  {panelStatus?.helperChannelId ? `#${panelStatus.helperChannelId}` : 'Non configurato'}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Stato embed:</span>{' '}
                  {panelStatus?.spawned ? '✅ Spawnato in chat' : '❌ Non spawnato'}
                </p>
                {panelStatus?.messageUrl && (
                  <a
                    href={panelStatus.messageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary underline"
                  >
                    Apri messaggio spawnato
                  </a>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              onClick={handleSpawnPanel}
              disabled={spawnPanelMutation.isPending || !selectedRegulation || !pluginConfig.helperChannelId}
              className="w-full"
            >
              {spawnPanelMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Spawn in corso...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  Spawna Embed in Chat
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => refetchPanelStatus()}
              disabled={panelStatusLoading}
              className="w-full"
            >
              {panelStatusLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                'Aggiorna Status'
              )}
            </Button>
          </div>

          {!pluginConfig.helperChannelId && (
            <p className="text-sm text-yellow-500">
              ⚠️ Configura prima il Canale AI Helper per poter spawnare l'embed.
            </p>
          )}

          {!selectedRegulation && (
            <p className="text-sm text-yellow-500">
              ⚠️ Crea o seleziona un regolamento per poter spawnare il pannello AI Helper.
            </p>
          )}

          {spawnPanelMutation.isSuccess && (
            <p className="text-sm text-green-500">✅ Embed AI Helper pubblicato con successo!</p>
          )}
        </CardContent>
      </Card>

      {/* Rules Embed Spawn & Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <CardTitle>Spawn Regolamento in Chat</CardTitle>
          </div>
          <CardDescription>
            Pubblica l'embed del regolamento e verifica se è già presente nel canale regolamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg border bg-muted/30 space-y-2">
            {rulesEmbedStatusLoading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Caricamento stato regolamento...
              </p>
            ) : (
              <>
                <p className="text-sm">
                  <span className="font-medium">Canale Regolamento:</span>{' '}
                  {rulesEmbedStatus?.rulesChannelId ? `#${rulesEmbedStatus.rulesChannelId}` : 'Non configurato'}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Stato embed:</span>{' '}
                  {rulesEmbedStatus?.spawned ? '✅ Spawnato in chat' : '❌ Non spawnato'}
                </p>
                {rulesEmbedStatus?.messageUrl && (
                  <a
                    href={rulesEmbedStatus.messageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary underline"
                  >
                    Apri messaggio regolamento
                  </a>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              onClick={handleSpawnRulesEmbed}
              disabled={spawnRulesEmbedMutation.isPending || !selectedRegulation || !effectiveRulesChannelId}
              className="w-full"
            >
              {spawnRulesEmbedMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Spawn in corso...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Spawna Regolamento
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => refetchRulesEmbedStatus()}
              disabled={rulesEmbedStatusLoading}
              className="w-full"
            >
              {rulesEmbedStatusLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                'Aggiorna Status'
              )}
            </Button>
          </div>

          {!effectiveRulesChannelId && (
            <p className="text-sm text-yellow-500">
              ⚠️ Configura prima un canale regolamento globale oppure un canale specifico per questo regolamento.
            </p>
          )}

          {!selectedRegulation && (
            <p className="text-sm text-yellow-500">
              ⚠️ Crea o seleziona un regolamento per poter spawnare l'embed del regolamento.
            </p>
          )}

          {spawnRulesEmbedMutation.isSuccess && (
            <p className="text-sm text-green-500">✅ Embed regolamento pubblicato con successo!</p>
          )}
        </CardContent>
      </Card>

      {/* Test AI */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            <CardTitle>Testa l'AI Helper</CardTitle>
          </div>
          <CardDescription>
            Prova a fare una domanda all'AI per verificare che funzioni correttamente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Domanda di Test</label>
            <Textarea
              value={testQuestion}
              onChange={(e) => setTestQuestion(e.target.value)}
              placeholder="Es: Quali sono le regole sulla pubblicità?"
              className="bg-background"
              rows={3}
            />
          </div>

          <Button
            onClick={handleTestAI}
            disabled={testAIMutation.isPending || !selectedRegulation}
            className="w-full"
          >
            {testAIMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Chiedendo all'AI...
              </>
            ) : (
              <>
                <Bot className="w-4 h-4 mr-2" />
                Chiedi all'AI
              </>
            )}
          </Button>

          {!selectedRegulation && (
            <p className="text-sm text-yellow-500 text-center">
              ⚠️ Crea o seleziona un regolamento per testare l'AI
            </p>
          )}

          {testAnswer && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-sm font-medium mb-2">Risposta AI:</p>
              <p className="text-sm whitespace-pre-wrap">{testAnswer}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            <CardTitle>Come Usare l'AI Helper</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold">1</span>
            </div>
            <div>
              <p className="font-medium">Salva il Regolamento</p>
              <p className="text-sm text-muted-foreground">
                Scrivi il regolamento completo del server nel campo sopra e salvalo
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold">2</span>
            </div>
            <div>
              <p className="font-medium">Gli Utenti Usano il Comando</p>
              <p className="text-sm text-muted-foreground">
                Gli utenti possono usare <code className="bg-muted px-1 py-0.5 rounded">/ai-helper</code> in Discord
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold">3</span>
            </div>
            <div>
              <p className="font-medium">L'AI Risponde</p>
              <p className="text-sm text-muted-foreground">
                L'AI legge il regolamento e risponde alle domande in modo accurato
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
